import { base64FromBlob, canSaveFileNative, saveFileNative } from './native-shell';

/**
 * Trigger a browser download of an in-memory blob — a CSV fetched with the
 * auth header (which can't be a plain navigation), or a file generated
 * client-side such as the cashier's .xlsx / .pdf exports.
 *
 * Lives in @mlm/ui because it is the browser-only shared package; @mlm/shared
 * and @mlm/db stay DOM-free for the API. Import it by subpath
 * (`@mlm/ui/lib/download`) so no consumer drags in the component barrel.
 *
 * The anchor MUST be appended to the document before `click()` — several
 * browsers ignore the `download` attribute on a detached anchor and fall back to
 * naming the file after the blob URL's UUID (which shows up as a blank/garbage
 * name). Revoking the object URL is likewise deferred until after the click.
 *
 * In the packaged Android app that anchor is INERT — a WebView has no download
 * handler for a blob: URL, so the click did nothing at all, with no error. Every
 * caller here (the member's data export, the certificate PNG, the referral QR,
 * the admin exports) was silently broken in the app. The native branch writes
 * the bytes and opens the share sheet instead. It is deliberately fire-and-
 * forget so the signature stays `void` for the callers that do not await.
 */
export function downloadBlob(
  data: BlobPart | Uint8Array,
  filename: string,
  mime = 'application/octet-stream',
): void {
  // TypeScript >= 5.7 types `Uint8Array` as `Uint8Array<ArrayBufferLike>`,
  // which no longer satisfies `BlobPart` (the buffer could in principle be a
  // SharedArrayBuffer). Generated files — the .xlsx writer's output — arrive as
  // exactly that, so copy into a plain ArrayBuffer instead of casting the
  // problem away. `slice()` is a copy; the cost is one pass over a file we just
  // built in memory.
  const part: BlobPart = data instanceof Uint8Array ? (data.slice().buffer as ArrayBuffer) : data;
  const blob = new Blob([part], { type: mime });

  if (canSaveFileNative()) {
    void base64FromBlob(blob).then((base64) => saveFileNative({ filename, base64 }));
    return;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
